import {
  BudgetModel,
  BudgetStatus,
  LineItemAmount,
  LineItemWithAmounts,
  Program,
  Project,
  TimeFrame,
} from '../../../utils/customTypes';
import { ParentOfLineItems } from '../../../utils/types/budget';

import isEmpty from 'lodash/isEmpty';
import { MONTHS_CAP } from '../../../utils/constants';
import {
  BUDGET_TABLE_FILTERS,
  BudgetFilters,
  BudgetFiltersKey,
} from '../../../utils/constants/budget';

export function numberFormat(number: number | string): string {
  if (!number) {
    return '0.00';
  }

  if (typeof number === 'string') {
    number = parseFloat(number);
  }

  return number.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export const formatCurrency = (amount: number) =>
  '$' + numberFormat(amount ?? 0);

export function mapRowColorWithLayer(layer: number): string {
  let answer;
  switch (layer) {
    case 0:
      answer = 'bg-table-layer2';
      break;
    case 1:
      answer = 'bg-table-layer2';
      break;
    case 2:
      answer = 'bg-neutral-lightest-two';
      break;
    default:
      answer = 'bg-bright-two';
  }
  return answer;
}

export const filterBudgetLineItems: (
  lineItems: LineItemWithAmounts[],
  filters: BudgetFilters
) => LineItemWithAmounts[] = (lineItems, filters) => {
  let filteredLineItems = lineItems;
  const filtersActive = Object.keys(filters).length > 0;
  if (filtersActive) {
    filteredLineItems = lineItems.filter((lineItem: LineItemWithAmounts) => {
      let programMatchFilters = false;
      programMatchFilters = lineItemMatchAllFilters(lineItem, filters);
      return programMatchFilters;
    });
  }
  return filteredLineItems;
};

export const lineItemMatchAllFilters: (
  lineItem: LineItemWithAmounts,
  filters: BudgetFilters
) => boolean = (lineItem, filters) => {
  let allFiltersMatched = true;
  for (const filter of Object.keys(filters)) {
    const filterValue = filters[filter as BudgetFiltersKey];
    const lineItemValue = lineItem[filter as keyof LineItemWithAmounts];
    if (!isEmpty(filterValue)) {
      let lineItemMatchesCurrentFilter = false;

      switch (filter) {
        case BUDGET_TABLE_FILTERS.PROGRAMS:
          lineItemMatchesCurrentFilter = (filterValue as string[]).includes(
            lineItem.programId ?? ''
          );
          break;
        case BUDGET_TABLE_FILTERS.PROJECTS:
          lineItemMatchesCurrentFilter = (filterValue as string[]).includes(
            lineItem.projectId ?? ''
          );
          // lineItemMatchesCurrentFilter = false;
          break;
        case BUDGET_TABLE_FILTERS.BUDGET_SOURCES:
          lineItemMatchesCurrentFilter = (filterValue as string[]).includes(
            lineItem.budgetSource ?? ''
          );
          break;
        case BUDGET_TABLE_FILTERS.VENDORS:
          lineItemMatchesCurrentFilter = (filterValue as string[]).includes(
            lineItem.vendorId ?? ''
          );
          break;
        case BUDGET_TABLE_FILTERS.TEAMS:
          lineItemMatchesCurrentFilter = (filterValue as string[]).includes(
            lineItem.learningTeamId ?? ''
          );
          break;
        case BUDGET_TABLE_FILTERS.ADDITIONAL_FLAGS:
          lineItemMatchesCurrentFilter = true;
          break;
        default:
          lineItemMatchesCurrentFilter = (filterValue as string[]).includes(
            lineItemValue as string
          );
          break;
      }

      allFiltersMatched = allFiltersMatched && lineItemMatchesCurrentFilter;
      if (!allFiltersMatched) {
        return allFiltersMatched;
      }
    } else {
      allFiltersMatched = false;
      return allFiltersMatched;
    }
  }
  return allFiltersMatched;
};

// Line item amounts can be sparse: the API only returns rows for months that
// have data, and a month edited through actuals-only flows can have a null
// allocation. Amounts must therefore be placed by month index (zero-filled
// elsewhere), never by array position.
export const buildMonthlyRawValues = (
  amounts: LineItemAmount[] | undefined,
  field: 'allocation' | 'actual'
): number[] => {
  const rawValues = Array(12).fill(0);
  amounts?.forEach((amount) => {
    const monthIndex =
      typeof amount.monthIndex === 'number'
        ? amount.monthIndex
        : MONTHS_CAP.indexOf(amount.month);
    if (monthIndex >= 0 && monthIndex < 12) {
      rawValues[monthIndex] = amount[field] ?? 0;
    }
  });
  return rawValues;
};

const buildYearlyRawValues = (
  amounts: LineItemAmount[] | undefined,
  field: 'allocation' | 'actual'
): number[] => {
  const total =
    amounts?.reduce((sum, amount) => sum + (amount[field] || 0), 0) || 0;
  return [total, ...Array(11).fill(0)];
};

const buildQuarterlyRawValues = (
  amounts: LineItemAmount[] | undefined,
  field: 'allocation' | 'actual',
  fiscalMonthStart: number
): number[] => {
  const monthlyValues = buildMonthlyRawValues(amounts, field);
  const quarterValues: number[] = [];

  for (let i = 0; i < 12; i += 3) {
    const quarterValue = Array.from({ length: 3 })
      .map((_, index) => (i + fiscalMonthStart + index) % 12) // Calculate the
      // actual month index (wrap around)
      .reduce((sum, monthIndex) => sum + monthlyValues[monthIndex], 0);
    quarterValues.push(quarterValue);
  }

  return [...quarterValues, ...Array(12 - quarterValues.length).fill(0)];
};

const calculateRawValuesForTimeFrame = (
  lineItem: LineItemWithAmounts,
  budget: BudgetModel
): Pick<LineItemWithAmounts, 'rawAmounts' | 'rawActuals'> | null => {
  if (budget.timeFrame === TimeFrame.MONTHLY) {
    return {
      rawAmounts: buildMonthlyRawValues(lineItem.amounts, 'allocation'),
      rawActuals: buildMonthlyRawValues(lineItem.amounts, 'actual'),
    };
  }
  if (budget.timeFrame === TimeFrame.YEARLY) {
    return {
      rawAmounts: buildYearlyRawValues(lineItem.amounts, 'allocation'),
      rawActuals: buildYearlyRawValues(lineItem.amounts, 'actual'),
    };
  }
  if (budget.timeFrame === TimeFrame.QUARTERLY) {
    const fiscalMonthStart: number = new Date(
      budget.fiscalPeriodStart
    ).getUTCMonth();
    return {
      rawAmounts: buildQuarterlyRawValues(
        lineItem.amounts,
        'allocation',
        fiscalMonthStart
      ),
      rawActuals: buildQuarterlyRawValues(
        lineItem.amounts,
        'actual',
        fiscalMonthStart
      ),
    };
  }
  return null;
};

export const calculateAmountsOfLineItems: (
  lineItems: LineItemWithAmounts[],
  budget: BudgetModel
) => LineItemWithAmounts[] = (lineItems, budget) => {
  lineItems.forEach((item) => {
    const rawValues = calculateRawValuesForTimeFrame(item, budget);
    if (rawValues) {
      item.rawAmounts = rawValues.rawAmounts;
      item.rawActuals = rawValues.rawActuals;
    }
  });
  return lineItems;
};

export const calculateAmountsOfSingleLineItem: (
  lineItem: LineItemWithAmounts,
  budget: BudgetModel
) => LineItemWithAmounts = (lineItem, budget) => {
  // rawActuals is recomputed alongside rawAmounts: the updateLineItem response
  // this runs on carries fresh amount rows (with actuals) from the API.
  return { ...lineItem, ...calculateRawValuesForTimeFrame(lineItem, budget) };
};

export const calculateTimeframeCellValue: (
  parentInfo: ParentOfLineItems,
  timeframe: TimeFrame,
  fiscalMonthStart: number,
  index: number
) => number = (parent, timeframe, fiscalMonthStart, index) => {
  let sum = 0;

  // Add the current parent's raw amount
  if (parent.rawAmounts && parent.rawAmounts.length > index) {
    sum +=
      timeframe === TimeFrame.MONTHLY
        ? parent.rawAmounts[(fiscalMonthStart + index) % 12]
        : parent.rawAmounts[index];
  }

  // Recursively add the raw amounts of children
  if (parent.children && parent.children.length > 0) {
    for (const child of parent.children) {
      sum += calculateTimeframeCellValue(
        child,
        timeframe,
        fiscalMonthStart,
        index
      );
    }
  }
  return sum;
};

export const calculateTotalAllocatedTimeframeCellValue: (
  lineItemInfos: LineItemWithAmounts[],
  timeframe: TimeFrame,
  fiscalMonthStart: number,
  index: number
) => number = (lineItemInfos, timeframe, fiscalMonthStart, index) => {
  let sum = 0;

  lineItemInfos.forEach((lineItem) => {
    if (lineItem.rawAmounts && lineItem.rawAmounts.length > 0) {
      if (timeframe === TimeFrame.MONTHLY) {
        sum += lineItem.rawAmounts[(fiscalMonthStart + index) % 12] || 0;
      } else if (timeframe === TimeFrame.QUARTERLY) {
        sum += lineItem.rawAmounts[index] || 0;
      } else {
        sum += lineItem.rawAmounts[index] || 0;
      }
    }
  });

  return sum;
};

export const calculateTotalAnnualValue: (
  parentInfo: ParentOfLineItems
) => number = (parentInfo) => {
  // Recursive function to calculate total annual value
  const calculateAnnualValue = (parent: ParentOfLineItems): number => {
    // Calculate the sum of the parent's rawAmounts
    const parentSum = parent.rawAmounts
      ? parent.rawAmounts.reduce(
          (total: number, item: number) => total + (item ?? 0),
          0
        )
      : 0;

    // Recursively calculate the sum for children
    const childrenSum = parent.children
      ? parent.children.reduce(
          (total: number, child: ParentOfLineItems) =>
            total + calculateAnnualValue(child),
          0
        )
      : 0;

    // Return total sum (parent + children)
    return parentSum + childrenSum;
  };

  return calculateAnnualValue(parentInfo);
};

export function buildLineItemData<T extends { id: string; parent_id?: string }>(
  entities: T[],
  lineItems: LineItemWithAmounts[],
  lineItemKey: keyof LineItemWithAmounts,
  titleKey: keyof T // Specify the field name for the title
): {
  newEntities: ParentOfLineItems[];
  entityMap: Record<string, ParentOfLineItems>;
} {
  const entityMap: Record<string, ParentOfLineItems> = {};
  const newEntities: ParentOfLineItems[] = [];

  // Map all entities by ID
  entities.forEach((entity) => {
    let newEntity: ParentOfLineItems = {
      id: entity.id,
      title: entity[titleKey] as unknown as string,
      parentId: entity.parent_id,
      type: lineItemKey as string,
      rawAmounts: Array(12).fill(0),
      rawActuals: Array(12).fill(0),
    };

    newEntity.children = [];
    newEntity.lineItems = lineItems.filter(
      (lineItem) => lineItem[lineItemKey] === entity.id
    );

    newEntity.rawAmounts = calculateRawTotalsFromLineItems(
      newEntity.lineItems,
      'rawAmounts'
    );
    newEntity.rawActuals = calculateRawTotalsFromLineItems(
      newEntity.lineItems,
      'rawActuals'
    );

    newEntities.push(newEntity);
    entityMap[entity.id] = newEntity;
  });

  return { newEntities, entityMap };
}

export function buildLineItemDataForPrograms(
  programs: Program[],
  projects: Project[],
  lineItems: LineItemWithAmounts[]
): {
  rootEntities: ParentOfLineItems[];
  allEntities: ParentOfLineItems[];
} {
  const rootEntities: ParentOfLineItems[] = [];
  const allEntities: ParentOfLineItems[] = [];
  let inProgramProjectIds = new Set<string>();
  let projectIds = projects.map((project) => project.id);

  // Map all entities by ID
  programs.forEach((program) => {
    let projectProgramIds = program.programProjects.map(
      (project) => project.id
    );
    let newEntity: ParentOfLineItems = {
      id: program.id,
      title: program.title,
      parentId: undefined,
      type: 'programId',
      rawAmounts: Array(12).fill(0),
      rawActuals: Array(12).fill(0),
      children: [],
    };

    newEntity.lineItems = lineItems.filter(
      (lineItem) =>
        lineItem.programId === program.id &&
        (lineItem.projectId
          ? !projectProgramIds.includes(lineItem.projectId)
          : true)
    );

    newEntity.rawAmounts = calculateRawTotalsFromLineItems(
      newEntity.lineItems,
      'rawAmounts'
    );
    newEntity.rawActuals = calculateRawTotalsFromLineItems(
      newEntity.lineItems,
      'rawActuals'
    );
    allEntities.push({ ...newEntity });

    newEntity.children = !program.programProjects
      ? []
      : program.programProjects.map((projectDetail) => {
          inProgramProjectIds.add(projectDetail.id);
          projectIds = projectIds.filter((id) => id !== projectDetail.id);

          let childProject: ParentOfLineItems = {
            id: projectDetail.id,
            title: projectDetail.title,
            parentId: program.id,
            type: 'projectId',
            rawAmounts: Array(12).fill(0),
            rawActuals: Array(12).fill(0),
            children: [],
          };

          childProject.lineItems = lineItems.filter((lineItem) => {
            return (
              (lineItem.projectId === projectDetail.id &&
                lineItem.programId === program.id) ||
              (lineItem.projectId === projectDetail.id &&
                lineItem.description === 'Cost of Labor')
            );
          });

          childProject.rawAmounts = calculateRawTotalsFromLineItems(
            childProject.lineItems,
            'rawAmounts'
          );
          childProject.rawActuals = calculateRawTotalsFromLineItems(
            childProject.lineItems,
            'rawActuals'
          );
          allEntities.push({ ...childProject });
          return childProject;
        });

    rootEntities.push(newEntity);
    // entityMap[entity.id] = newEntity;
  });
  projects.forEach((project) => {
    if (inProgramProjectIds.has(project.id)) {
      return;
    }
    let newEntity: ParentOfLineItems = {
      id: project.id,
      title: project.title,
      parentId: undefined,
      type: 'projectId',
      rawAmounts: Array(12).fill(0),
      rawActuals: Array(12).fill(0),
      children: [],
    };
    newEntity.lineItems = lineItems.filter(
      (lineItem) =>
        lineItem.projectId === project.id && lineItem.programId == null
    );

    newEntity.rawAmounts = calculateRawTotalsFromLineItems(
      newEntity.lineItems,
      'rawAmounts'
    );
    newEntity.rawActuals = calculateRawTotalsFromLineItems(
      newEntity.lineItems,
      'rawActuals'
    );

    allEntities.push({ ...newEntity });
    rootEntities.push(newEntity);
  });

  return { rootEntities, allEntities };
}

export function calculateRawTotalsFromLineItems(
  lineItems: LineItemWithAmounts[],
  field: keyof LineItemWithAmounts
) {
  return lineItems.reduce((sums: number[], item) => {
    const values = item[field] as number[];
    if (Array.isArray(values)) {
      values.forEach((amount: number, index: number) => {
        sums[index] = ((sums[index] || 0) + amount) as number;
      });
    }
    return sums;
  }, Array(12).fill(0));
}
export function buildHierarchyFromLineItemEntities(
  entities: ParentOfLineItems[],
  entityMap: Record<string, ParentOfLineItems>
): ParentOfLineItems[] {
  const rootEntities: ParentOfLineItems[] = [];

  entities.forEach((entity) => {
    const { parentId } = entity;

    if (parentId && entityMap[parentId]) {
      // Initialize children if missing (extra safety)
      if (!entityMap[parentId].children) {
        entityMap[parentId].children = [];
      }
      entityMap[parentId].children?.push(entity);
    } else {
      // Entity has no parent: it's a root
      rootEntities.push(entity);
    }
  });

  return rootEntities;
}

export function sortBudgets(budgetList: BudgetModel[]): BudgetModel[] {
  return budgetList.sort((a, b) => {
    // First compare by fiscal period year (descending)
    const dateComparison =
      new Date(b.fiscalPeriodStart).getFullYear() -
      new Date(a.fiscalPeriodStart).getFullYear();

    // If same year, sort by status priority
    if (dateComparison === 0) {
      const getStatusPriority = (status: BudgetStatus) => {
        switch (status) {
          case BudgetStatus.ACTIVE:
            return 0;
          case BudgetStatus.DRAFT:
            return 1;
          case BudgetStatus.ARCHIVED:
            return 2;
          default:
            return 3;
        }
      };

      return getStatusPriority(a.status) - getStatusPriority(b.status);
    }

    return dateComparison;
  });
}

/**
 * Returns a budget's fiscal year from its `fiscalPeriodStart` using the UTC year.
 * `fiscalPeriodStart` is a UTC instant (e.g. a January start is `YYYY-01-01T00:00:00Z`),
 * so it must be read with `getUTCFullYear()`. Reading it with local-time `getFullYear()`
 * yields the previous year in timezones behind UTC and breaks fiscal-year matching (CGP-70).
 */
export const getBudgetFiscalYear = (fiscalPeriodStart: string): number =>
  new Date(fiscalPeriodStart).getUTCFullYear();
